import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { gql } from '__generated__';

import { EditUserPermissionsForm } from './EditPermissionsForm';
import { emailRegex, getServerErrors, isGraphQLAuthenticatorRequiredError } from '../../common/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import GetUsersForProjectQuery from './GetUsersForProjectQuery';
import UI from '../../common/UI';
import useAuthenticatorVerificationModal from '../../common/useAuthenticatorVerificationModal';
import useMutation from '../../api/useMutation';
import useProject from '../useProject';
import useValidation from '../../common/useValidation';

const addUserSchema = yup.object().shape({
  email: yup.string().required().matches(emailRegex, { excludeEmptyString: true, name: 'email' }),
  permissions: yup.array().min(1).required(),
});

export const AddUserToProjectMutation = gql(`
  mutation AddUserToProject($input: AddUserToProjectInput!) {
    addUserToProject(input: $input) {
      id
    }
  }
`);

export interface AddUserModalProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  refetchQueries?: DocumentNode[];
}

export const AddUserModal = ({
  onSuccess, onCancel, refetchQueries = [GetUsersForProjectQuery],
}: AddUserModalProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const project = useProject();

  const form = useForm({
    values: {
      email: '',
      permissions: [],
    },
  });

  const [showAuthenticatorVerificationModal] = useAuthenticatorVerificationModal({
    onSuccess: () => addUser(),
  });

  const [addUser, { error, loading }] = useMutation(
    AddUserToProjectMutation, {
      variables: {
        input: {
          project_id: project.id,
          ...form.values,
        },
      },
      onCompleted: () => {
        notifier.success(t('notifications.user_added'));
        onSuccess?.();
      },
      onError: (error) => {
        if (isGraphQLAuthenticatorRequiredError(error)) {
          showAuthenticatorVerificationModal();
        }
      },
      refetchQueries,
      awaitRefetchQueries: refetchQueries.length > 0,
    },
  );

  const { valid, errors } = useValidation({
    schema: addUserSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => addUser()}>
      <UI.FormGrid>
        <UI.H4>
          {t('add_user')}
        </UI.H4>

        {!isGraphQLAuthenticatorRequiredError(error) && <UI.ServerErrors error={error} />}

        <UI.HR />

        <UI.InputGroup
          sc={{ valid: !errors.email, touched: form.touched.email, mb: 0 }}
        >
          <UI.InputLabel htmlFor="EmailAddress">
            {t('email_address')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.InputDescription>
            {t('add_user_email_address_description')}
          </UI.InputDescription>
          <UI.DebouncedInput id="EmailAddress" {...form.text('email')} />
          <UI.ErrorMessages attribute={t('email_address')} errors={errors.email} />
        </UI.InputGroup>

        <EditUserPermissionsForm form={form} errors={errors} />

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('add_user')}
          onClose={onCancel}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

const useAddUserModal = () => {
  const [openModal, closeModal] = useModal(() => (
    <UI.Modal maxWidth={600} close={closeModal}>
      <AddUserModal onSuccess={closeModal} onCancel={closeModal} />
    </UI.Modal>
  ), []);

  return [openModal, closeModal];
};

export default useAddUserModal;
